<template>
  <div>
    <div v-if="onEditing || errors" class="input-group mb-3">
      <input
        ref="refInput"
        v-model="input"
        :type="type"
        :min="minDate"
        :max="maxDate"
        :maxlength="max"
        class="form-control"
        :class="{ 'is-invalid': errors }"
        :placeholder="placeholder"
        @change="edited"
        @keyup.enter="edited"
        @keydown.esc="onEditing = false"
        @keypress="onKeyPress"
        @input="onInput"
      />
      <b-form-invalid-feedback>
        <p v-if="errors">{{ errors }}</p>
      </b-form-invalid-feedback>
    </div>

    <div v-else class="input-group mb-3 border-bottom">
      <div v-if="type == 'date'">
        <input
          type="text"
          class="form-control-plaintext text-muted"
          :value="dateformat(value)"
          readonly
        />
      </div>
      <div v-else>
        <input
          :type="type"
          class="form-control-plaintext text-muted"
          :value="maskReadOnlyValue(value)"
          readonly
        />
      </div>
      <div v-if="isEditable" class="input-group-append">
        <button
          :disabled="isDisabled"
          class="btn btn-link text-csq"
          type="button"
          @click="editing"
        >
          <b-icon-pencil variant="secondary" />
        </button>
      </div>
      <div v-if="name === 'idCard' || name === 'phoneNumber'" class="icon-toggle" style="cursor: pointer;" @click="toggleMask">
        <b-icon v-if="isMasked" icon="eye-slash"></b-icon>
          <b-icon v-else icon="eye"></b-icon>
        </div>
    </div>
  </div>
</template>

<script>
import { BFormInvalidFeedback } from "bootstrap-vue";
import dateMixins from "@/components/pages/analytics/shared/mixins/date.js";

export default {
  components: { BFormInvalidFeedback },
  mixins: [dateMixins],
  props: {
    name: { type: String, default: "" },
    value: { type: String, default: "" },
    isEditable: { type: Boolean, default: true },
    type: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    minDate: { type: String, default: null },
    maxDate: { type: String, default: null },
    max:{ type: String, default: null },
    errors: { type: String, default: "" },
    isDisabled: { type: Boolean, default: false },
    errorText: {type: String, default: ""},
    gregorianYear: { type: Boolean, default: false },
    required: { type: Boolean, default: false }
  },

  data() {
    return {
      onEditing: true,
      input: "",
      isMasked: true,
    };
  },

  mounted() {
    if (this.value != "") this.onEditing = false;
    this.input = this.value;
  },

  watch: {
    value(newVal) {
      this.input = newVal;
    }
  },

  methods: {
    editing() {
      this.onEditing = true;
      this.input = this.value;
    },

    inserted() {
      this.onEditing = true;
      this.$emit("edited", { name: this.name, value: this.input });
    },

    edited() {
      if (!this.input) {
        this.input = "";
        this.errors = this.errorText;
        // Required fields are never saved empty; stay in edit mode with the error.
        if (this.required) return;
      } else if (this.name === "idCard" && !/^\d{13}$/.test(this.input)) {
        // Invalid ID card: show the error and restore the original value without saving.
        this.errors = "กรุณากรอกเลขประจำตัวประชาชน 13 หลัก";
        this.input = this.value;
        return;
      } else if (this.type === "date" && this.maxDate && this.input > this.maxDate) {
        this.errors = "วันที่ต้องไม่เกินวันปัจจุบัน";
        this.input = "";
      } else if (
        (this.name == "phoneNumber" || this.name == "parentPhone") &&
        (this.input.length < 9 || this.input.length > 15)
      ) {
        this.errors = "กรุณากรอกเบอร์โทรศัพท์ให้ถูกต้อง";
      } else if (
        this.name == "postcode" &&
        this.input.length > 5
      ) {
        this.errors = "รหัสไปรษณีย์ต้องไม่เกิน 5 หลัก";
      } else if (this.name === 'email') {
        this.input = this.input.toLowerCase();
      } else {
        this.errors = "";
      }
      this.onEditing = false;
      this.$emit("edited", { name: this.name, value: this.input });
    },
    onKeyPress(event) {
      const char = String.fromCharCode(event.which || event.keyCode);
      const charCode = event.which ? event.which : event.keyCode;

      if (this.type === "number" || this.name === 'phoneNumber' || this.name === 'parentPhone' || this.name === 'postcode' || this.name === 'idCard') {
        if (charCode > 31 && (charCode < 48 || charCode > 57)) {
          event.preventDefault();
        }
      }
      if (this.name === 'firstNameEn' || this.name === 'lastNameEn') {
        if (!/^[A-Za-z\s]$/.test(char)) {
          this.errors = 'กรุณากรอกเป็นภาษาอังกฤษ';
          event.preventDefault();
        } else {
          this.errors = '';
        }
      }
      if (this.name === 'firstName' || this.name === 'lastName') {
        if (!/^[฀-๿\s]$/.test(char)) {
          this.errors = 'กรุณากรอกเป็นภาษาไทย';
          event.preventDefault();
        } else {
          this.errors = '';
        }
      }
    },
    onInput() {
      if (this.name === 'firstName' || this.name === 'lastName') {
        const sanitized = this.input.replace(/[^฀-๿\s]/g, '');
        if (sanitized !== this.input) {
          this.input = sanitized;
          this.errors = 'กรุณากรอกเป็นภาษาไทย';
        }
      }
      if (this.name === 'firstNameEn' || this.name === 'lastNameEn') {
        const sanitized = this.input.replace(/[^A-Za-z\s]/g, '');
        if (sanitized !== this.input) {
          this.input = sanitized;
          this.errors = 'กรุณากรอกเป็นภาษาอังกฤษ';
        }
      }
      if (this.name === 'phoneNumber' || this.name === 'parentPhone' || this.name === 'postcode' || this.name === 'idCard') {
        this.input = this.input.replace(/[^\d]/g, '');
      }
    },
    dateformat(date) {
      if (date == "9999-12-31") {
        return "-";
      } else {
        return this.thaiDateFilter(date, false, this.gregorianYear);
      }
    },
    toggleMask() {
      this.isMasked = !this.isMasked;
    },
    maskReadOnlyValue(val) {
      // If there's no value, or it's NOT the idCard field, show it normally
      if (!val || (this.name !== 'idCard' && this.name !== 'phoneNumber')) {
        return val;
      }

      const strVal = String(val);

      if (!this.isMasked) {
        return val;
      }

      // If the length is greater than 4, mask all but the last 4 digits
      if (strVal.length > 4) {
        const visiblePart = strVal.slice(-4);
        const maskedPart = '*'.repeat(strVal.length - 4);
        return maskedPart + visiblePart;
      }

      // If it's 4 characters or less, completely mask it
      return '*'.repeat(strVal.length);
    },
  },
};
</script>

<style scoped lang="scss">
.input-group {
  align-items: center;
}
</style>
